import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { en } from "./en";
import { mr } from "./mr";
import type { Translations } from "./en";

export type Lang = "en" | "mr";

const STORAGE_KEY = "geosurvey_admin_lang";

const DICTS: Record<Lang, Translations> = { en, mr };

function getStoredLang(): Lang {
  if (typeof window === "undefined") return "en";
  return localStorage.getItem(STORAGE_KEY) === "mr" ? "mr" : "en";
}

type LanguageContextValue = {
  lang: Lang;
  t: Translations;
  setLang: (lang: Lang) => void;
};

const LanguageContext = createContext<LanguageContextValue | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(getStoredLang);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  function setLang(next: Lang) {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Private window / blocked site data: the preference just won't survive
      // a reload, which is a harmless degradation here.
    }
    setLangState(next);
  }

  const value = useMemo<LanguageContextValue>(
    () => ({ lang, t: DICTS[lang], setLang }),
    [lang]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage() used outside <LanguageProvider>");
  return ctx;
}
